<template>
	<div class="car-item" :class="theme" v-on="$listeners">
		<div>
			<ratio-div class="img-container" :ratio="theme === 'large' ? ratioData : 1">
				<nio-image
					v-if="theme !== 'large'"
					class="car-img"
					:imageStyle="{ 'object-fit': isHeadPhotoFallback ? 'contain' : 'cover' }"
					:src="photos.length ? cdn(photos[0].thpf_url, '/thumbnail/!300x300r', true) : ''"
				/>

				<van-swipe v-else @change="onSwipe" v-bind="vanAttrs" ref="swiper">
					<template v-if="vanAttrs.width">
						<van-swipe-item v-for="(item, index) in photos" :key="item.thpf_url + index">
							<nio-image
								class="large-car-image"
								:imageStyle="{ 'object-fit': 'cover' }"
								:src="item.thpf_url"
							/>
						</van-swipe-item>
					</template>

					<template #indicator>
						<div class="indi-area" v-if="photos.length > 1">
							<div
								v-for="(item, idx) in photos"
								:key="item.thpf_url + idx"
								class="indi-item"
								:class="{ cur: idx === currentPhoto }"
							></div>
						</div>
					</template>
				</van-swipe>
				<div v-if="!!disabledTips" class="disabled-tips">
					{{ disabledTips }}
				</div>

				<div class="icon-container" @click.stop="handleCollect(collected)" v-if="showCollect">
					<img v-if="!collected" class="no-collected" :src="IconStar" />
					<img v-else class="collected" :src="IconStarFill" />
				</div>
			</ratio-div>
		</div>

		<div class="car-info">
			<car-tags :tags="carTags" :theme="theme" />

			<div class="car-name">
				{{ carName }}
			</div>

			<div v-if="theme !== 'large'" class="car-sub-rows">
				<div class="car-sub">
					<span v-for="item in basicInfoArr" :key="item" class="sub-item">
						{{ item }}
					</span>
				</div>
				<div class="car-sub">
					<span v-for="item in usageInfoArr" :key="item" class="sub-item">
						{{ item }}
					</span>
				</div>
			</div>
			<div v-else class="car-sub-rows">
				<div class="car-sub">
					<span v-for="item in basicInfoArr.concat(usageInfoArr)" :key="item" class="sub-item">
						{{ item }}
					</span>
				</div>
				<div class="car-sub">
					<span v-for="item in appearanceInfoArr" :key="item" class="sub-item">
						{{ item }}
					</span>
				</div>
			</div>

			<div class="price-line">
				<span class="price-value">
					<span class="price-unit">¥</span>
					{{ formatCurrency(car.price) }}
				</span>
				<span class="price-sub">{{ car.location_city_name }}</span>
			</div>
		</div>

		<div v-if="disabled" class="disabled-mask"></div>
	</div>
</template>

<script>
import RatioDiv from '@/components/RatioDiv'
import CarTags, { businessTypeMapTag } from '@/components/CarTags'
import {
	allCarDisabledStatus,
	allCarStatus,
	allInteriorColors
} from '@/views1/IndexPage/filterOptions'
import util from '@/util/util'
import { mapActions, mapState } from 'vuex'
import hybrid from '@/util/nativeHybrid'
import { cdn } from '@/util/imgCdn'
import { sortAlbumAppearance, sortAlbumInterior } from '@/views1/DetailPage/sortRules'
import { BrandMap, BrandEnum } from '@/util/constant'
import { getFastListingPhotos, isFastListing as checkFastListing } from '@/util/fast-listing'
import IconStar from '@/assets/images/Star.png'
import IconStarFill from '@/assets/images/Star_fill.png'

export default {
	name: 'CarItem',
	components: { CarTags, RatioDiv },
	props: {
		car: {
			type: Object,
			required: true
		},
		theme: {
			type: String,
			default: 'normal'
		},

		highlight: {
			type: Boolean,
			default: false
		},
		showCollect: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			loading: false,
			currentPhoto: 0,
			vanAttrs: {},
			IconStar,
			IconStarFill,
			ratioData: (document.body.clientWidth - 40) / 256
		}
	},
	computed: {
		...mapState('homeState', {
			collectedRecord: state => state.collectedRecord,
			cancelCollectRecord: state => state.cancelCollectRecord
		}),
		collected() {
			if (!this.showCollect) {
				return false
			}

			if (!util.isToken()) {
				return false
			}

			if (this.collectedRecord.indexOf(this.car.commodity_code) >= 0) {
				return true
			}

			if (this.cancelCollectRecord.indexOf(this.car.commodity_code) >= 0) {
				return false
			}
			return this.car.is_favorite
		},
		disabled() {
			return allCarDisabledStatus.some(one => one.value === this.car.status)
		},

		disabledTips() {
			if (!allCarStatus.some(one => one.value === this.car.status)) {
				return null
			}

			return allCarStatus.find(one => one.value === this.car.status).label
		},
		carTags() {
			const tags = []

			const businessType = this.car.business_type
			const tag = businessTypeMapTag[businessType]
			tag && tags.push(tag)

			if (this.car.tags && this.car.tags.length) {
				this.car.tags.forEach(({ tag_name, tag_colour }) => {
					tags.push({ label: tag_name, type: tag_colour })
				})
			}

			return tags
		},
		basicInfoArr() {
			const arr = []
			// if (this.car.first_plate_time) {
			// 	arr.push(dayjs(this.car.first_plate_time * 1000).format('YYYY') + '年上牌')
			// }

			if (this.car.battery_desc) {
				arr.push(this.car.battery_desc)
			}

			if (this.car.is_baas) {
				arr.push('电池租用')
			} else {
				arr.push('整车购买')
			}
			return arr
		},

		usageInfoArr() {
			const arr = []

			if (this.car.age) {
				arr.push(util.formatAge(this.car.age))
			}

			if (this.car.mileage) {
				arr.push(util.formatMileage(this.car.mileage))
			}

			return arr
		},

		appearanceInfoArr() {
			const arr = []

			if (this.car.car_paint_desc) {
				arr.push(this.car.car_paint_desc + '外观')
			}
			if (this.car.interior_color && allInteriorColors[this.car.interior_color]) {
				arr.push(allInteriorColors[this.car.interior_color])
			}
			const highlight = (this.car.highlight_config || []).concat().splice(0, 6)
			arr.push(...highlight)
			return arr
		},
		isFastListing() {
			return checkFastListing(this.car)
		},
		photos() {
			if (this.isFastListing) {
				return getFastListingPhotos(this.car)
			}

			const appearances = sortAlbumAppearance(this.car.appearances)

			const interiors = sortAlbumInterior(this.car.interior)

			return [...appearances, ...interiors]
		},
		isHeadPhotoFallback() {
			return Boolean(this.photos[0] && this.photos[0].isHeadPhotoFallback)
		},
		carName() {
			const name = (this.car.commodity_name || '').replace(/ET5 Touring/g, 'ET5T')
			const brand = BrandMap[this.car.brand || BrandEnum.NIO]
			return brand ? brand + ' ' + name : name
		}
	},
	methods: {
		...mapActions('homeState', ['switchCollect']),
		cdn,
		formatCurrency: util.formatCurrency,

		onSwipe(idx) {
			this.currentPhoto = idx
		},
		async handleCollect(curCollected) {
			if (this.loading) {
				return
			}

			if (!util.isToken()) {
				hybrid.openLoginPage(() => {
					this.$emit('login')
				})

				return
			}

			this.loading = true

			try {
				const commodity_code = this.car.commodity_code

				await this.switchCollect({ commodity_code, curCollected })

				if (curCollected) {
					this.$toast('已取消收藏')
				} else {
					this.$toast('收藏成功')
				}

				this.$emit('collect', !curCollected)
			} catch (e) {
				this.$toast(e.message)
			} finally {
				setTimeout(() => {
					this.loading = false
				}, 1500)
			}
		},
		measureSwiper() {
			this.$nextTick(() => {
				if (this.$refs.swiper) {
					const width = getComputedStyle(this.$refs.swiper.$el).width.replace('px', '')
					this.vanAttrs = {
						width: +width
					}
				}
			})
		}
	},
	mounted() {
		this.measureSwiper()
	},
	watch: {
		theme() {
			this.measureSwiper()
		}
	}
}
</script>

<style lang="scss">
@import '~@nio-fe/nio-app-ui/es/style';

.car-list-wrapper {
	> li.normal {
		display: inline-block;
		width: calc(50vw - 27.5px);
		margin-bottom: 25px;

		&:nth-child(odd) {
			margin-right: 15px;
		}
	}
}

.car-item {
	position: relative;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	margin-bottom: 15px;

	* {
		flex-shrink: 0;
		flex-grow: 0;
	}

	&.normal {
		display: flex;
		overflow: hidden;

		.car-info .car-name {
			@include subtitle_4(1.5, 500, false);
		}

		.img-container {
			width: 100%;
			margin-right: 15px;
			margin-bottom: 10px;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
				object-position: center;
			}
		}

		.car-info {
			overflow: hidden;
		}

		.car-sub {
			margin-bottom: 2px;
		}

		.price-line {
			margin-top: 15px;
		}

		.disabled-tips {
			font-size: 12px;
			width: 52px;
			height: 25px;
		}
	}

	&.large {
		.car-name {
			font-size: 18px;
			line-height: 25px;
			font-weight: 500;
		}

		.img-container {
			width: 100%;

			.large-car-image {
				width: 100%;
				height: 256px;
				object-fit: cover;
			}
		}

		.car-info {
			padding: 15px 0 35px;
			overflow: hidden;

			.car-name {
				margin-bottom: 5px;
			}
			.car-sub {
				overflow: hidden;
				margin-bottom: 2px;
			}

			.price-line {
				margin-top: 17px;
				align-items: center;
				.price-value {
					font-style: normal;
					font-size: 18px;
					line-height: 21px;
				}

				.price-sub {
					//display: none;
				}
			}
		}

		.disabled-tips {
			font-size: 14px;
			width: 72px;
			height: 30px;
		}
	}

	&.horizontal {
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		margin-bottom: 0px;

		.img-container {
			width: 120px;
			height: 120px;
			margin-right: 15px;

			.car-img {
				width: 100%;
				height: 100%;
			}
		}

		.car-info {
			.car-name {
				@include subtitle_4(1.5, 500, false);
			}

			.car-sub-rows {
				margin-bottom: 10px;

				.car-sub {
					&:not(:last-child) {
						margin-bottom: 2px;
					}
				}
			}
		}

		.disabled-tips {
			font-size: 12px;
			width: 52px;
			height: 25px;
		}
	}

	&.highlight {
		.price-value {
			color: $text_basic_active;
		}
	}
	.img-container {
		position: relative;
		overflow: hidden;

		.car-img {
			width: 100%;
			height: 100%;
		}
		.disabled-tips {
			position: absolute;

			left: 10px;
			top: 10px;

			background: rgba(42, 59, 122, 0.25);
			backdrop-filter: blur(15px);

			display: flex;
			align-items: center;
			justify-content: center;
			color: white;
		}

		.icon-container {
			position: absolute;
			top: 10px;
			right: 10px;
			width: 25px;
			height: 25px;
			background-color: rgba(0, 0, 0, 0.1);
			display: flex;
			align-items: center;
			justify-content: center;

			> img {
				position: static;
				width: 16px;
				height: 16px;
				object-fit: contain;

				&.no-collected {
					color: $border_disable;
				}

				&.collected {
					color: white;
				}
			}
		}
	}

	.car-info {
		position: relative;
		flex: 1;
		min-width: 0;
		flex-shrink: 1;
		height: 100%;

		.car-name {
			color: $text_primary;
			margin-bottom: 2px;
			display: -webkit-box;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
			overflow: hidden;
			text-overflow: ellipsis;

			> :first-child {
				flex: 1;
				word-break: break-all;
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;
			}
		}

		.car-sub-rows {
			margin-bottom: 8px;
		}
		.car-sub {
			@include body_3(1.5, 400, false);

			overflow: hidden;
			color: $text_tertiary;
			white-space: nowrap;
			word-break: break-all;
			text-overflow: ellipsis;

			&:not(:last-child) {
				margin-bottom: 5px;
			}
			.sub-item {
				&:not(:last-child) {
					&:after {
						content: ' ';
						display: inline-block;
						height: 10px;
						border-right: 1px solid #e9ecf1;
						margin: 0 2px;
					}
				}
			}
		}

		.price-line {
			display: flex;
			overflow: hidden;
			white-space: nowrap;
			align-items: center;
			justify-content: flex-start;

			.price-value {
				color: $text_primary;
				@include subtitle_3(1.2, bold);
				font-weight: 500;
				flex: 1;

				.price-unit {
					transform-origin: left top;
					transform: scale(0.8) translateY(2px);
					margin-right: -5px;
					display: inline-block;
				}
			}

			.price-sub {
				@include body_3(1.2, 400, false);
				min-width: 0;
				margin-left: 5px;
				overflow: hidden;
				text-overflow: ellipsis;
				flex-shrink: 1;
				color: $text_tertiary;
			}
		}
	}

	.disabled-mask {
		position: absolute;
		left: 121px;
		top: 0;
		right: 0;
		bottom: 0;
		background-color: #ffffff;
		opacity: 0.6;
		pointer-events: none;
	}

	.van-swipe {
		.indi-area {
			position: absolute;
			left: 0;
			right: 0;
			bottom: 20px;
			display: flex;
			align-items: center;
			justify-content: center;
		}
		.indi-item {
			width: 15px;
			height: 2px;
			margin-right: 5px;

			&.cur {
				background-color: #ffffff;
			}

			&:not(.cur) {
				background-color: rgba(255, 255, 255, 0.3);
			}
		}
		.van-swipe-item {
			text-align: center;
		}
	}
}
</style>
